/**
 * The reels' sounds (apps/mobile/src/assets/audio/slot), by alias: a soft
 * whoosh as they set off, a wooden whirr while they spin, and a wooden thunk
 * as each one lands, pitched a little higher reel by reel. When the spin
 * wins, the last reel lands with coins instead (producer 2026-10-16: "when
 * you win it should sound like coins landing"): a few for a small win, a
 * spill for a medium one and a cascade for a big one. A spin that wins
 * nothing ends on the last reel's own thunk: the fuller clunk it once had
 * is gone (producer 2026-11-03: "we don't need it"). The slot machine plays
 * them on every spin, bonus respins included.
 */
export const REEL_SOUNDS = {
  start: 'slot:spin_start',
  loop: 'slot:spin_loop',
  /** Where the whirr's seamless stretch sits in its file, in seconds: it
   *  is padded either side so decoder padding never reaches the loop. */
  loopStart: 0.08,
  loopLength: 1.75,
  /** How loud the whirr plays while every reel spins (it thins out as
   *  they land). */
  loopVolume: 1,
  /** Reel `index` (0 to 4) landing; the last to land too, when the spin won nothing. */
  land: (index: number): string => `slot:reel_land_${Math.min(5, index + 1)}`,
  /** The last reel landing on a winning spin, by the size of the win. */
  coins: {
    small: 'slot:coins_land_small',
    medium: 'slot:coins_land_medium',
    big: 'slot:coins_land_big',
  },
} as const;

export type CoinLanding = keyof typeof REEL_SOUNDS.coins;

/**
 * Where each coin landing starts, as a multiple of the bet: under twice
 * the bet a few coins land, a spill from twice, and a cascade from five
 * times (where the BIG WIN headline starts too).
 */
export const COIN_LANDING_FROM: Readonly<Record<CoinLanding, number>> = { small: 0, medium: 2, big: 5 };

/** Which coins land for a win of `multiple` times the bet; null for none. */
export function coinLanding(multiple: number | undefined): CoinLanding | null {
  if (multiple === undefined || !(multiple > 0)) return null;
  if (multiple >= COIN_LANDING_FROM.big) return 'big';
  if (multiple >= COIN_LANDING_FROM.medium) return 'medium';
  return 'small';
}

/** The sound the last reel, reel `index`, lands with, for a spin that won `multiple` times its bet (0 or unknown:
 *  its own thunk, as the others). */
export function finalLanding(multiple: number | undefined, index: number): string {
  const coins = coinLanding(multiple);
  return coins ? REEL_SOUNDS.coins[coins] : REEL_SOUNDS.land(index);
}
